import viewModes from 'shared/constants/view-modes';
import defaultLocale from 'shared/constants/default-locale';
import requestLoginModes from 'shared/constants/request-login-modes';
import DEVICE_TYPES from 'shared/constants/device-types';

import { invokeSafely } from './attempt';
import xorGuids from './xor-guids';
import settings from '../settings';


const IMAGE_SOURCE_NAME = null;
const JPEG_PARAMS = {
  q: 85,
  usm: '0.66_1.00_0.01',
};

function forceSSL(func) {
  return function () {
    const url = func(...arguments);
    return url.replace(/^http:\/\//, 'https://');
  };
}

function isJPEG(extension) {
  return /^jpe?g$/i.test(extension);
}


const WixSDKOriginal = window.Wix;

const WixSDK = WixSDKOriginal ? {
  ...WixSDKOriginal,
  addEventListener: _.get(WixSDKOriginal, 'addEventListener', _.noop),
  currentMember() {
    if (WixSDKOriginal.Utils.getViewMode() === viewModes.SITE) {
      return new Promise(WixSDKOriginal.currentMember);
    }

    return Promise.resolve(null);
  },
  getCtToken() {
    return new Promise(WixSDKOriginal.getCtToken)
      .then(ctToken => {
        if (!ctToken) {
          return Promise.reject(null);
        }

        return ctToken;
      });
  },
  removeEventListener: WixSDKOriginal.removeEventListener || _.noop,
  requestLogin(params) {
    return new Promise(_.partial(WixSDKOriginal.requestLogin, {
      mode: requestLoginModes.SIGN_UP,
      language: WixSDK.Utils.getLocale(),
      ...params,
    }))
      .catch(function (error) {
        if (error && error.wasCancelled) {
          return;
        }

        return Promise.reject(error);
      });
  },
  getBoundingRectAndOffsets() {
    return new Promise(WixSDKOriginal.getBoundingRectAndOffsets);
  },
  Activities: {
    ...WixSDKOriginal.Activities,
    getUserSessionToken() {
      return new Promise(WixSDKOriginal.Activities.getUserSessionToken);
    },
  },
  Dashboard: {
    ...WixSDKOriginal.Dashboard,
    getEditorUrl() {
      const getEditorUrl = _.get(WixSDKOriginal, 'Dashboard.getEditorUrl');
      if (getEditorUrl) {
        return new Promise(getEditorUrl);
      }

      return Promise.resolve(null);
    },
  },
  Events: {
    ...WixSDKOriginal.Events,
  },
  PubSub: {
    ...WixSDKOriginal.PubSub,
    subscribe: _.get(WixSDKOriginal, 'PubSub.subscribe', _.noop),
    unsubscribe: _.get(WixSDKOriginal, 'PubSub.unsubscribe', _.noop),
  },
  Settings: {
    ...WixSDKOriginal.Settings,
    setExternalId(id) {
      return new Promise((resolve, reject) => WixSDKOriginal.Settings.setExternalId(id, resolve, reject));
    },
    getSiteInfo() {
      const viewMode = WixSDKOriginal.Utils.getViewMode();

      if (viewMode === viewModes.STANDALONE || viewMode === viewModes.DASHBOARD) {
        return Promise.resolve({
          url: window.location.href,
        });
      }

      return new Promise(WixSDKOriginal.Settings.getSiteInfo);
    },
    getDashboardAppUrl() {
      return new Promise(WixSDKOriginal.Settings.getDashboardAppUrl);
    },
  },
  Styles: {
    ...WixSDKOriginal.Styles,
    getStyleParams() {
      return new Promise(WixSDKOriginal.Styles.getStyleParams);
    },
    setFontParam(key, value) {
      return new Promise(_.partial(WixSDKOriginal.Styles.setFontParam, key, { value }));
    },
    setStyleParams(params) {
      return new Promise(_.partial(WixSDKOriginal.Styles.setStyleParams, params));
    },
  },
  Utils: {
    ...WixSDKOriginal.Utils,
    getDeviceType: _.get(WixSDKOriginal, 'Utils.getDeviceType', _.constant(DEVICE_TYPES.DESKTOP)),
    getLocale() {
      const locale = WixSDKOriginal.Utils.getLocale();
      if (!locale || locale.length > 2) {
        // prevent xss
        return defaultLocale;
      }
      return locale;
    },
    getInstance() {
      try {
        return location.search.match(/(?:\?|&)instance=([^&]+)/)[1];
      } catch (error) {
        return null;
      }
    },
    getInstanceId: invokeSafely(global.WMP.wixUtils.getInstanceId, null),
    getInstanceValue: invokeSafely(function (key) {
      // if biToken is missing for any reason, global.WMP.wixUtils.getBiToken will generate fake one
      if (key === 'biToken') {
        return global.WMP.wixUtils.getBiToken();
      }

      const result = WixSDKOriginal.Utils.getInstanceValue(key);
      if (key === 'siteOwnerId' && !result) {
        return WixSDKOriginal.Utils.getInstanceValue('uid');
      }
      return result;
    }),
    getMetaSiteIdFromBiToken() {
      let biToken;
      let instanceId;
      try {
        biToken = WixSDK.Utils.getInstanceValue('biToken');
        instanceId = WixSDK.Utils.getInstanceId();
      } catch (error) {
        // do nothing
      }

      if (biToken && instanceId) {
        return xorGuids(biToken, instanceId);
      }
    },
    getMetaSiteId() {
      const metaSiteId = WixSDK.Utils.getMetaSiteIdFromBiToken();
      if (metaSiteId) {
        return Promise.resolve(metaSiteId);
      }


      const viewMode = WixSDK.Utils.getViewMode();
      if (viewMode === viewModes.STANDALONE) {
        return Promise.reject('Unable to get metaSiteId in standalone view mode');
      }

      if (viewMode === viewModes.EDITOR) {
        return WixSDK.Settings.getDashboardAppUrl()
          .then(url => url.match(/\/sites\/([^/]+)/)[1]);
      }

      return WixSDK.Dashboard.getEditorUrl()
        .then(url => url.match(/metaSiteId=([^&]+)/)[1]);
    },
    getUid() {
      try {
        return WixSDKOriginal.Utils.getUid();
      } catch (error) {
      }

      try {
        return document.cookie.match(/wixClient=([^;]+)/)[1].split('|')[6];
      } catch (error) {
      }

      return null;
    },
    getViewMode: _.get(WixSDKOriginal, 'Utils.getViewMode', _.noop),
    isEditor() {
      return WixSDK.Utils.getViewMode() === viewModes.EDITOR;
    },
    Media: {
      getImageUrl(relativeUrl) {
        return `${settings.hosts.image}${relativeUrl}`;
      },
      getResizedImageUrl: forceSSL(function (url, width, height = width, params = {}) {
        if (!url) {
          return '';
        }

        width = Math.round(width);
        height = Math.round(height);

        if (/vimeocdn/.test(url)) {
          return url.replace(/(_\d+(x\d+)?)?\.jpg/, `_${width}x${height}.jpg`);
        }

        if (/^(https?:)?\/\//.test(url)) {
          return url;
        }

        const fullFilename = url.split('/').pop();
        const imagePath = url.replace(fullFilename, '');

        const filenameSplitted = fullFilename.split('.');
        const filename = filenameSplitted.slice(0, -1).join('.');
        const ext = filenameSplitted[filenameSplitted.length - 1];
        const imageUrlEnd = IMAGE_SOURCE_NAME ? `${filename}_${IMAGE_SOURCE_NAME}.${ext}` : `${filename}.${ext}`;

        params = {
          ...params,
          w: width,
          h: height,
        };

        if (isJPEG(ext)) {
          params = { ...params, ...JPEG_PARAMS };
        }

        const imageParamsPath = _.map(params, (value, key) => `${key}_${value}`).join(',');

        const fullImagePath = `${imagePath}v1/fill/${imageParamsPath}/${imageUrlEnd}`;
        return WixSDK.Utils.Media.getImageUrl(fullImagePath);
      }),
    },
  },
} : null;

export default WixSDK;



// WEBPACK FOOTER //
// ./src/shared/utils/wix-sdk.js